import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import {
  limitOrderAPI,
  scheduledOrderAPI,
  marketsAPI,
  getErrorMessage,
} from '../../utils/api';
import {
  formatCurrency,
  formatDate,
  formatStatusText,
} from '../../utils/helpers';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  RefreshCw,
  ArrowDown,
  ArrowUp,
  ListOrdered,
  CalendarClock,
} from 'lucide-react';

const STATUS_TONE = {
  active:    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  triggered: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  paused:    'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  cancelled: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  expired:   'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  failed:    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const StatusBadge = ({ status }) => {
  const tone = STATUS_TONE[status?.toLowerCase?.()] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400';
  return (
    <span
      className={`${tone} text-xs px-2 py-0.5 rounded-full font-medium inline-block`}
    >
      {formatStatusText(status || '')}
    </span>
  );
};

const TabButton = ({ active, onClick, children }) =>
  active ? (
    <span className="px-4 py-2 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-medium text-sm shadow-sm">
      {children}
    </span>
  ) : (
    <button
      type="button"
      onClick={onClick}
      className="px-4 py-2 rounded-md text-gray-500 dark:text-gray-400 text-sm hover:text-gray-700 dark:hover:text-gray-200 cursor-pointer transition-colors"
    >
      {children}
    </button>
  );

const Orders = () => {
  const [tab, setTab] = useState('limit');
  const [limitOrders, setLimitOrders] = useState([]);
  const [scheduledOrders, setScheduledOrders] = useState([]);
  const [prices, setPrices] = useState({}); // symbol -> current_price
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null); // {kind, id}

  const loadAll = useCallback(async () => {
    try {
      const [limitRes, schedRes, marketsRes] = await Promise.all([
        limitOrderAPI.getAll(),
        scheduledOrderAPI.getAll(),
        marketsAPI.getCryptocurrencies().catch(() => ({ data: [] })),
      ]);
      setLimitOrders(Array.isArray(limitRes.data) ? limitRes.data : []);
      setScheduledOrders(Array.isArray(schedRes.data) ? schedRes.data : []);
      const priceMap = {};
      (marketsRes.data || []).forEach((c) => {
        if (c.symbol) priceMap[c.symbol.toUpperCase()] = c.current_price;
      });
      setPrices(priceMap);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load orders'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    const { kind, id } = cancelTarget;
    setCancelTarget(null);
    try {
      if (kind === 'limit') {
        await limitOrderAPI.cancel(id);
        setLimitOrders((prev) =>
          prev.map((o) => (o.id === id ? { ...o, status: 'cancelled' } : o))
        );
      } else {
        await scheduledOrderAPI.cancel(id);
        setScheduledOrders((prev) => prev.filter((o) => o.id !== id));
      }
      toast.success('Order cancelled');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to cancel order'));
    }
  };

  const limitRows = useMemo(
    () =>
      [...limitOrders].sort(
        (a, b) =>
          new Date(b.created_at || 0).getTime() -
          new Date(a.created_at || 0).getTime()
      ),
    [limitOrders]
  );

  const schedRows = useMemo(
    () =>
      [...scheduledOrders].sort(
        (a, b) =>
          new Date(a.next_execution || 0).getTime() -
          new Date(b.next_execution || 0).getTime()
      ),
    [scheduledOrders]
  );

  if (loading)
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="loading-spinner"></div>
      </div>
    );

  return (
    <div className="space-y-6 animate-fadeIn p-3 sm:p-4 lg:p-6" data-testid="orders-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Orders</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
            Manage your limit and scheduled orders
          </p>
        </div>
        <button
          onClick={loadAll}
          className="flex items-center gap-2 px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm transition-colors"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg w-fit mb-5">
        <TabButton active={tab === 'limit'} onClick={() => setTab('limit')}>
          Limit Orders ({limitRows.length})
        </TabButton>
        <TabButton
          active={tab === 'scheduled'}
          onClick={() => setTab('scheduled')}
        >
          Scheduled Orders ({schedRows.length})
        </TabButton>
      </div>

      {tab === 'limit' && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            {limitRows.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-3">
                  <ListOrdered size={20} className="text-gray-400 dark:text-gray-500" />
                </div>
                <p className="text-gray-900 dark:text-white font-medium">No limit orders yet</p>
                <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                  Place a limit order from the Exchange page
                </p>
              </div>
            ) : (
              <table className="w-full min-w-[640px]">
                <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Symbol
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Side
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Target Price
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Current Price
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      % Distance
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Amount
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Status
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Created
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide w-[110px]">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {limitRows.map((o) => {
                    const symbol = (o.asset_symbol || '').toUpperCase();
                    const current =
                      o.current_price ?? prices[symbol] ?? null;
                    const target = Number(o.target_price);
                    const distancePct =
                      current != null && target
                        ? ((current - target) / target) * 100
                        : null;
                    const side = (o.trade_type || '').toLowerCase();
                    const cancellable = o.status === 'active';
                    return (
                      <tr
                        key={o.id}
                        className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors last:border-0"
                      >
                        <td className="px-5 py-4 text-sm text-gray-900 dark:text-white font-medium">
                          {symbol}
                        </td>
                        <td className="px-5 py-4 text-sm">
                          <span
                            className={`inline-flex items-center text-xs font-semibold uppercase ${
                              side === 'buy'
                                ? 'text-green-600'
                                : 'text-red-600'
                            }`}
                          >
                            {side === 'buy' ? (
                              <ArrowDown className="w-3 h-3 mr-1" />
                            ) : (
                              <ArrowUp className="w-3 h-3 mr-1" />
                            )}
                            {side || '—'}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-900 dark:text-white text-right">
                          {formatCurrency(target)}
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-900 dark:text-white text-right">
                          {current != null ? formatCurrency(current) : '—'}
                        </td>
                        <td
                          className={`px-5 py-4 text-sm text-right ${
                            distancePct == null
                              ? 'text-gray-500 dark:text-gray-400'
                              : distancePct >= 0
                              ? 'text-green-600'
                              : 'text-red-600'
                          }`}
                        >
                          {distancePct == null
                            ? '—'
                            : `${distancePct >= 0 ? '+' : ''}${distancePct.toFixed(2)}%`}
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-900 dark:text-white text-right">
                          {o.amount}
                        </td>
                        <td className="px-5 py-4 text-sm">
                          <StatusBadge status={o.status} />
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(o.created_at)}
                        </td>
                        <td className="px-5 py-4 text-right">
                          {cancellable && (
                            <button
                              type="button"
                              onClick={() =>
                                setCancelTarget({ kind: 'limit', id: o.id })
                              }
                              className="text-xs px-3 py-1 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                            >
                              Cancel
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {tab === 'scheduled' && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            {schedRows.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-3">
                  <CalendarClock size={20} className="text-gray-400 dark:text-gray-500" />
                </div>
                <p className="text-gray-900 dark:text-white font-medium">
                  No scheduled orders yet
                </p>
                <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                  Set up a recurring buy from the Exchange page
                </p>
              </div>
            ) : (
              <table className="w-full min-w-[640px]">
                <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Symbol
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Side
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Amount
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Frequency
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Next Execution
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Last Execution
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Status
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide w-[110px]">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {schedRows.map((o) => {
                    const symbol = (o.asset_symbol || '').toUpperCase();
                    const side = (o.trade_type || '').toLowerCase();
                    const status =
                      o.is_active === false ? 'paused' : 'active';
                    return (
                      <tr
                        key={o.id}
                        className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors last:border-0"
                      >
                        <td className="px-5 py-4 text-sm text-gray-900 dark:text-white font-medium">
                          {symbol}
                        </td>
                        <td className="px-5 py-4 text-sm">
                          <span
                            className={`text-xs font-semibold uppercase ${
                              side === 'buy'
                                ? 'text-green-600'
                                : 'text-red-600'
                            }`}
                          >
                            {side || '—'}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-900 dark:text-white text-right">
                          {o.amount}
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-900 dark:text-white capitalize">
                          {o.frequency || '—'}
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {o.next_execution
                            ? formatDate(o.next_execution)
                            : '—'}
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {o.last_execution
                            ? formatDate(o.last_execution)
                            : '—'}
                        </td>
                        <td className="px-5 py-4 text-sm">
                          <StatusBadge status={status} />
                        </td>
                        <td className="px-5 py-4 text-right">
                          <button
                            type="button"
                            onClick={() =>
                              setCancelTarget({ kind: 'scheduled', id: o.id })
                            }
                            className="text-xs px-3 py-1 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          >
                            Cancel
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      <AlertDialog
        open={cancelTarget !== null}
        onOpenChange={(open) => {
          if (!open) setCancelTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel order?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. The order will be cancelled and any
              reserved balance released.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep order</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancel}>
              Cancel order
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Orders;
